<template>
  <div class="view-wrapper is-initiative-detail">
    <ViewHeader
      :title="viewTitle"
      titleLabel="Nested Initiative Variant"
      :hideSectionHomeButton="true"
      :isFullViewport="true"
      :hideRoleTutorial="true"
    >
      <template #icon>
        <span class="pi pi-link"></span>
      </template>
      <template #actions>
        <Button
          @click="handleDeleteInitiative($event)"
          class="p-button-text p-mr-auto"
          icon="pi pi-trash delete-icon"
          v-if="!isCreateInitiativeCustomization && canUserDeleteInitiative"
        />
        <Button
          @click="showCancelConfirmation($event)"
          class="p-mr-2 p-button-outlined"
          :label="saveButtonDisabled ? 'Close' : 'Cancel'"
        />
        <Button
          id="btn_tactic_save"
          :class="saveButtonDisabled ? 'p-disabled' : null"
          :disabled="saveButtonDisabled"
          @click="handleSaveInitiativeCustomization"
          label="Save"
        ></Button>
      </template>
    </ViewHeader>
    <ViewMain :isFullViewport="true">
      <div class="initiative-detail-wrapper">
        <div class="details-container">
          <div class="p-d-flex">
            <div class="p-field p-mr-2">
              <label for="name">Name</label>
              <InputText
                id="name" 
                v-model="localInitiative.title" 
                :disabled="true" 
              />
            </div>
            <div class="p-field p-mr-2">
              <label for="type">Category</label>
              <Dropdown
                inputId="type"
                v-model="selectedType"
                optionLabel="name"
                :options="typeOptions"
                :disabled="true"
              />
            </div>
          </div>
        </div>

        <div class="time-frame-container">
          <div class="p-d-flex p-ai-center" v-show="isMonthlyOnly">
            <div v-if="isMonthlyOnly" class="p-field p-mr-2">
              <label for="start">Month</label>
              <Calendar
                id="start"
                v-model="localInitiativeCustomization.startDate"
                view="month"
                dateFormat="mm/yy"
                :yearNavigator="true"
                :manualInput="false"
                :yearRange="$store.getters.datePickerYearRange"
                :showTime="false"
                appendTo="body"
                @hide="
                  () => {
                    onMonthlyFocusDateSelect()
                    onInputChange()
                    nonConditionalChange()
                  }
                "
                :disabled="!canUserEditFields"
              />
            </div>
          </div>
          <div class="p-d-flex p-ai-center" v-if="!isMonthlyOnly">
            <div class="p-field p-mr-2" v-if="!localInitiative.isUnscheduled">
              <label for="start">Start Date</label>
              <Calendar
                id="start"
                v-model="localInitiativeCustomization.startDate"
                :manualInput="false"
                :monthNavigator="true"
                :yearNavigator="true"
                :yearRange="$store.getters.datePickerYearRange"
                :showTime="shouldShowTimeInput"
                hourFormat="12"
                dateFormat="m/d/yy"
                appendTo="body"
                @hide="
                  () => {
                    onInputChange()
                    nonConditionalChange()
                    // clearRepeat()
                  }
                "
                :disabled="!canUserEditFields"
              >
                <template #footer v-if="shouldShowTimeInput">
                  <TimePicker
                    :date="currentInitiative.startDate"
                    :setDate="updateCurrentInitiativeStartDate"
                  />
                </template>
              </Calendar>
            </div>
            <div
              v-if="shouldShowEndDateInput && !localInitiative.isUnscheduled"
              class="p-field p-mr-2"
            >
              <label for="end">End Date</label>
              <Calendar
                id="end"
                v-model="localInitiativeCustomization.endDate"
                :manualInput="false"
                :monthNavigator="true"
                :yearNavigator="true"
                :yearRange="$store.getters.datePickerYearRange"
                :showTime="shouldShowTimeInput"
                hourFormat="12"
                dateFormat="m/d/yy"
                appendTo="body"
                @hide="
                  () => {
                    onInputChange()
                    nonConditionalChange()
                  }
                "
                :disabled="!canUserEditFields"
              >
                <template #footer v-if="shouldShowTimeInput">
                  <TimePicker
                    :date="currentInitiative.endDate"
                    :setDate="updateCurrentInitiativeEndDate"
                  />
                </template>
              </Calendar>
            </div>
            <div class="p-ml-4 p-mr-4" v-if="!localInitiative.isUnscheduled">
              <Checkbox
                id="hasEndDate"
                class="p-mr-2"
                v-model="shouldShowEndDateInput"
                :binary="true"
                :disabled="true"
              />
              <label for="hasEndDate">Show Duration</label>
            </div>
            <div class="p-ml-4 p-mr-4" v-if="!localInitiative.isUnscheduled">
              <Checkbox
                id="isAllDay"
                class="p-mr-2"
                v-model="localInitiativeCustomization.isAllDay"
                :binary="true"
                @change="
                  () => {
                    onAllDayChange()
                    nonConditionalChange()
                  }
                "
                :disabled="!canUserEditFields"
              />
              <label for="isAllDay">All-Day</label>
            </div>
          </div>
        </div>
        <div class="summary-container p-mt-6" v-if="currentPlan.isTacticSummaryFieldEnabled">
          <template v-if="localInitiative.summary && localInitiative.summary.length">
            <label for="summary">Lead Initiative Notes</label>
            <QuillEditor
              id="summary"
              class="p-mt-2 p-mb-4"
              v-model="localInitiative.summary"
              :disabled="true"
              :class="summaryInputStyleObject"
            ></QuillEditor>
          </template>

          <label for="additional_summary">{{ localInitiativeCustomization.abbreviatedPlanName }} | Notes</label>
          <QuillEditor
            id="additional_summary"
            class="p-mt-2"
            :key="localInitiativeCustomization.id.intID"
            v-model="localInitiativeCustomization.summary"
            @text-change="
              (event) => {
                nonConditionalChange()
                onEditorTextChange(event, 'notesMessage')
              }
            "
            :disabled="!canUserEditFields"
            :class="summaryInputStyleObject"
          ></QuillEditor>

          <div id="notesMessage"></div>
        </div>
      </div>

      <div>
        <div
          class="plan-elements-container"
          v-if="$store.getters.currentTags.length"
        >
          <hr class="p-my-4" />
          <div class="p-d-flex p-jc-between p-ai-start">
            <h2>Tags</h2>
            <Button
              label="Tags"
              icon="pi pi-plus-circle"
              @click="shouldShowTagList = true"
              :disabled="!canUserEditFields || !canUserEditTags"
            />
          </div>
          <div
            class="p-d-flex p-jc-start p-ai-start"
            style="width: 80%; min-width: 60rem"
          >
            <template
              v-for="(
                tagCategory, tagCategoryIndex
              ) in currentCategorizedTagsAssignedToInitiative"
            >
              <div
                class="tag-list-column p-mr-6"
                :key="tagCategoryIndex"
                v-show="tagCategory.tags.length"
              >
                <h3>{{ tagCategory.name }}</h3>
                <div class="p-d-flex p-flex-column p-ai-start">
                  <template v-for="tag of tagCategory.tags">
                    <div
                      :key="tag.id.intID"
                      class="p-d-flex p-ai-center p-mr-2 p-mb-2 tag-chip"
                    >
                      <div
                        class="p-mr-2"
                        :class="{
                          'is-nested': tag.isNested,
                          'is-lead': tag.isLead,
                        }"
                      >
                        <template v-if="isLeadPlan && tag.isNested">
                          {{ tag.abbreviatedPlanName }}  |  {{ tag.title }}
                        </template>
                        <template v-else>
                          {{ tag.title }}
                        </template>
                      </div>
                      <Button
                        v-if="!tag.isLead"
                        icon="pi pi-times-circle"
                        class="p-button-text"
                        @click="handleRemoveTag(tag.id)"
                        :disabled="!canUserEditTags"
                      />
                    </div>
                  </template>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <div class="initiative-detail-documents-container">
        <hr class="p-my-4" />
        <div class="p-d-flex p-jc-between p-ai-start">
          <h2>Documents</h2>
        </div>
        <DataTable
          :value="initiativeAndCustomizationDocuments"
          :autoLayout="true"
          v-if="initiativeAndCustomizationDocuments.length"
        >
          <Column header="Name" key="id.intID">
            <template #body="slotProps">
              {{ slotProps.data.asset.name }}
            </template>
          </Column>
          <Column>
            <template #body="slotProps">
              <div class="p-d-flex p-jc-end p-ai-center">
                <Button class="p-button-text p-button-icon-only">
                  <a
                    target="_blank"
                    :href="slotProps.data.asset.fileLocation"
                    download
                    style="color: #000"
                  >
                    <i class="pi pi-download"></i>
                  </a>
                </Button>
                <Button
                  v-if="localInitiativeCustomization.documents.filter((document) => document.id.intID === slotProps.data.id.intID).length > 0"
                  icon="pi pi-trash"
                  class="p-button-text"
                  @click="(e) => deleteDocument(e, slotProps.data.id.intID, localInitiativeCustomization)"
                ></Button>
              </div>
            </template>
          </Column>
        </DataTable>

        <div class="upload-document-container p-d-flex">
          <label
            for="document-upload"
            class="p-button p-component p-button-text"
            style="border: 0.1rem solid #000; border-radius: 0"
          >
            Select File
            <input
              id="document-upload"
              type="file"
              @change="(e) => onFileUpload(e, localInitiativeCustomization)"
              hidden
            />
          </label>
          <InputText v-model="localInitiativeCustomization.newFileName" />
          <Button
            label="Upload"
            icon="pi pi-upload"
            class="p-button-text"
            @click="uploadFile(localInitiativeCustomization)"
          ></Button>
        </div>
      </div>

      <div class="initiative-detail-supporting-tactics-container">
        <hr class="p-my-4" />
        <div class="p-d-flex p-jc-between p-ai-start">
          <h2>Supporting Tactics</h2>
        </div>
        <DataTable
          :value="supportingTactics"
          :autoLayout="true"
          v-if="supportingTactics.length"
        >
          <Column field="title" header="Topic" sortable>
            <template #body="slotProps">
              <span :class="{'is-lead': slotProps.data.isLead}">
                {{ getTitleForTactic(slotProps.data) }}
              </span>
            </template>
          </Column>
          <Column field="channelName" header="Channel" sortable>
            <template #body="slotProps">
              {{ slotProps.data.channelName }}
            </template>
          </Column>
          <Column field="tacticTypeName" header="Tactic Type" sortable>
            <template #body="slotProps">
              {{ slotProps.data.tacticTypeName }}
            </template>
          </Column>
          <Column field="tacticPlatformIds" header="Platform">
            <template #body="slotProps">
              {{ getPlatformNamesForTactic(slotProps.data) }}
            </template>
          </Column>
          <Column field="startDate" header="Start Date" sortable>
            <template #body="slotProps">
              {{ slotProps.data.startDate.toLocaleDateString() }}
            </template>
          </Column>
          <Column field="endDate" header="End Date" sortable>
            <template #body="slotProps">
              {{ slotProps.data.endDate.toLocaleDateString() }}
            </template>
          </Column>
          <Column header="Notes" key="summary">
            <template #body="slotProps">
              <Button
                v-if="
                  slotProps.data.summary !== null &&
                  slotProps.data.summary !== ''
                "
                icon="pi pi-eye"
                class="p-button-icon-only p-button-rounded p-button-text"
                @mouseenter="
                  (event) => {
                    currentOverlayHTML = slotProps.data.summary
                      ? slotProps.data.summary
                      : ''
                    $refs.richTextFieldOverlay?.show(event)
                  }
                "
                @mouseleave="
                  (event) => {
                    $refs.richTextFieldOverlay?.hide()
                  }
                "
              ></Button>
              <template v-else> None </template>
            </template>
          </Column>
          <Column>
            <template #body="slotProps">
              <div
                class="p-d-flex p-jc-end"
                v-if="
                  (isLeadPlan && slotProps.data.isLead) ||
                  (!isLeadPlan && !slotProps.data.isLead)
                "
              >
                <Button
                  label="Open"
                  icon="pi pi-arrow-circle-up"
                  class="p-button-text"
                  @click="
                    () => {
                      $router.push({
                        path: `/account/${$store.getters.currentAccount.id.intID}/plan/${$route.params.planId}/tactic/${slotProps.data.id.intID}`,
                      })
                    }
                  "
                ></Button>
              </div>
            </template>
          </Column>
        </DataTable>
        <div v-else>
          <p>No supporting tactics found.</p>
        </div>
      </div>

      <Dialog header="Tags" :visible.sync="shouldShowTagList" :modal="true">
        <TagList
          :selectedTags="localInitiativeCustomization.tags"
          :selectedDisabledTags="localInitiative.tags"
          :allowSelect="true"
          :allowEdit="false"
          :allowCreate="false"
          :allowList="true"
          :isInitiativeCustomization="true" 
          @updateSelection="onTagListUpdate"
        />
      </Dialog>
     
      <OverlayPanel
        ref="richTextFieldOverlay"
        class="rich-text-field-overlay"
        appendTo="body"
      >
        <div
          class="notes-field-content-container"
          v-html="currentOverlayHTML"
        ></div>
      </OverlayPanel>
    </ViewMain>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import Checkbox from 'primevue/checkbox'
import Dropdown from 'primevue/dropdown'
import MultiSelect from 'primevue/multiselect'
import Dialog from 'primevue/dialog'
import RadioButton from 'primevue/radiobutton'
import ViewHeader from '@/components/ViewHeader.vue'
import ViewMain from '@/components/ViewMain.vue'
import TagList from '@/views/TagList.vue'
import Tactic from '@/models/Tactic'
import Plan from '@/models/Plan'
import TacticPlatform from '@/models/TacticPlatform'
import ID from '@/models/ID'
import Tag from '@/models/Tag'
import {AlertMessageSeverity} from '@/models/AlertMessage'
import InputNumber from 'primevue/inputnumber'
import Button from 'primevue/button'
import SplitButton from 'primevue/splitbutton'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import TimePicker from '@/components/TimePicker.vue'
import FileUpload from 'primevue/fileupload'
import Role from '@/models/Role'
import TagCategory from '@/models/TagCategory'
import PlanDocument from '@/models/PlanDocument'
import RelatedInitiativeMapping from '@/models/RelatedInitiativeMapping'
import Tooltip from 'primevue/tooltip'
import QuillEditor from '@/components/QuillEditor.vue'
import Initiative from '@/models/Initiative'
import InitiativeChannel from '@/models/InitiativeChannel'
import InitiativeType from '@/models/InitiativeType'
import InitiativeCustomization from '@/models/InitiativeCustomization'
import { InitiativeSummaryData } from '@/models/InterfacePlanningItem'

Vue.component('Checkbox', Checkbox)
Vue.component('Dropdown', Dropdown)
Vue.component('MultiSelect', MultiSelect)
Vue.component('Dialog', Dialog)
Vue.component('InputNumber', InputNumber)
Vue.component('Button', Button)
Vue.component('SplitButton', SplitButton)
Vue.component('FileUpload', FileUpload)
Vue.component('TimePicker', TimePicker)
Vue.component('RadioButton', RadioButton)
Vue.component('DataTable', DataTable)
Vue.component('Column', Column)

export default Vue.extend({
  name: 'InitiativeCustomizationDetail',

  components: {
    ViewHeader,
    ViewMain,
    TagList,
    QuillEditor,
  },
  directives: {
    tooltip: Tooltip,
  },
  data: () => {
    return {
      localInitiative: {} as Initiative,
      localInitiativeCustomization: {} as InitiativeCustomization,

      selectedType: {} as InitiativeType,
      nonReactive: {} as any,
      shouldShowTagList: false as boolean,
      currentSelectInitiativeTypeName: '' as string,
      currentSelectInitiativeId: {} as ID,
      currentSelectInitiativeTitle: '' as string,
      shouldShowTimeInput: true as boolean,
      shouldShowEndDateInput: false as boolean,
      tagUpdateItterator: 0 as number,
      initialStartDate: '' as string,
      initialEndDate: '' as string,
      initialTacticName: '' as string,
      initialChannel: '' as string,
      initialType: '' as string,
      initialTags: {} as {},
      initialSummary: '' as string,
      saveButtonDisabled: true as boolean,
      invalidFields: [] as string[],
      editModeFieldIds: [] as number[],
      currentOverlayHTML: '' as string,
    }
  },
  computed: {
    currentStartDate: {
      get: function (): Date {
        return this.localInitiativeCustomization.startDate
      },
      set: function (newValue: Date) {
        this.localInitiativeCustomization.startDate = newValue
      },
    },
    supportingTactics(): Tactic[] {
      if (this.isCreateInitiativeCustomization) {
        return []
      }
      return this.$store.getters.currentPlan.tactics.filter((tactic) =>
        tactic.relatedInitiatives.map((mapping: RelatedInitiativeMapping) => mapping.initiativeId.intID).includes(this.localInitiativeCustomization.id.intID)
        && tactic.planId.intID === this.currentPlan.id.intID // Filter out related tactics that are not part of this nested plan
      )
    },
    viewTitle(): string {
      const tacticType = 'Initiative'
      const tacticTitle = this.localInitiative ? this.localInitiative.title : ''
      return this.isCreateInitiativeCustomization ? 'New ' + tacticType : tacticTitle
    },
    isCreateInitiative(): boolean {
      return this.$store.getters.currentInitiativeIntID == 0 ? true : false
    },
    currentPlan(): Plan {
      return this.$store.getters.currentPlan
    },
    isLeadPlan(): boolean {
      return this.$store.getters.currentPlan.plans.length ? true : false
    },
    currentInitiative(): Initiative {
      return this.localInitiative
    },
    currentCategorizedTagsAssignedToInitiative(): TagCategory[] {
      this.tagUpdateItterator
      // customization.tags already includes merged tags from parent initiative
      const localTagList = [...this.localInitiativeCustomization.tags]

      // Create categories
      const categories: TagCategory[] = []
      localTagList.forEach((tag) => {
        const categoryIndex = categories.findIndex(
          (cat) => cat.name === tag.type
        )
        if (categoryIndex === -1) {
          categories.push(new TagCategory(categories.length + 1, tag.type))
        }
      })
      // Add tags to categories
      localTagList.forEach((tag) => {
        const categoryIndex = categories.findIndex(
          (cat) => cat.name === tag.type
        )
        categories[categoryIndex === -1 ? 3 : categoryIndex].tags.push(tag)
      })
      // Sort categories alphabetically
      categories.sort((a, b) => {
        if (a.name === b.name) {
          return 0
        }
        return a.name > b.name ? 1 : -1
      })
      // Sort tags within categories according to orderIndex
      categories.forEach((tagCat) => {
        tagCat.tags.sort((a, b) => {
          if (a.orderIndex === b.orderIndex) {
            return 0
          }
          return a.orderIndex > b.orderIndex ? 1 : -1
        })
      })
      return categories
    },
    selectedChannel(): InitiativeChannel {
      return this.$store.getters.currentPlan.initiativeChannel
    },
    typeOptions(): InitiativeType[] {
      return [this.localInitiative.initiativeType]
    },
    isMonthlyOnly(): boolean {
      if (this.selectedType.name) {
        return (
          this.selectedType.name.toLowerCase() ==
            this.$store.getters.monthlyFocusTypeName ||
          this.selectedType.name.toLowerCase() ==
            InitiativeType.INITIATIVE_TYPE_NAME_MONTHLY_THEME
        )
      }
      return false
    },
    canUserEditChannel(): boolean {
      return true
    },
    canUserDeleteInitiative(): boolean {
      return this.$store.getters.currentPlanPermissionLevel <= Role.LEVEL_CONTRIBUTOR
    },
    canUserEditFields(): boolean {
      return (
        this.canUserEditChannel &&
        this.$store.getters.currentPlanPermissionLevel <= Role.LEVEL_CONTRIBUTOR
      )
    },
    canUserEditTags(): boolean {
      return this.$store.getters.currentPlanPermissionLevel <= Role.LEVEL_CONTRIBUTOR
    },
    summaryInputStyleObject(): {} {
      return {'disable-editor': !this.canUserEditFields}
    },
    isCreateInitiativeCustomization(): boolean {
      return Number(this.$route.params.customizationId) === 0
    },
    initiativeAndCustomizationDocuments(): PlanDocument[] {
      // customization.documents already includes merged documents from parent initiative
      return [...this.localInitiativeCustomization.documents]
    }
  },
  created: function() {
    this.$store.commit('updateIsUIBlocked', true)
    this.$store.commit('setCurrentInitiativeIntID', Number(this.$route.params.initiativeId))
    this.localInitiative = this.$store.getters.currentInitiative
    this.selectedType = this.localInitiative.initiativeType
    this.setInitialConditionalValues()

    if (!this.isCreateInitiativeCustomization) {
      const existingCustomization = this.localInitiative.initiativeCustomizations.find((customization) => customization.planId.intID === this.currentPlan.id.intID)
      this.localInitiativeCustomization = existingCustomization ? existingCustomization : new InitiativeCustomization()
    } else {
      this.localInitiativeCustomization = InitiativeCustomization.fromResponseObject({
        id: 0,
        creator: this.$store.getters.currentUser.id,
        initiative: this.localInitiative.id.intID,
        initiativeType: this.localInitiative.initiativeType.id.intID,
        plan: this.currentPlan.id.intID,
        startDate: new Date(this.localInitiative.startDateString),
        endDate: new Date(this.localInitiative.endDateString),
        isAllDay: this.localInitiative.isAllDay,
      })
    }

    // Fetch summary content from remote URLs
    const leadSummaryUrl = this.localInitiative.initiativeSummary?.contentUrl
    const customizationSummaryUrl = this.localInitiativeCustomization.additionalSummaryUrl

    const fetchPromises: Promise<void>[] = []

    if (leadSummaryUrl) {
      fetchPromises.push(
        this.$store.getters.services.initiatives
          .fetchSummaryContent(leadSummaryUrl)
          .then((html: string) => {
            this.localInitiative.summary = html
          })
      )
    }

    if (customizationSummaryUrl) {
      fetchPromises.push(
        this.$store.getters.services.initiatives
          .fetchSummaryContent(customizationSummaryUrl)
          .then((html: string) => {
            this.localInitiativeCustomization.summary = html
          })
      )
    }

    Promise.all(fetchPromises)
      .finally(() => {
        this.updateEndDateInputVisibility()
        this.setInitialValues()
        this.nonConditionalChange()
        this.$store.commit('updateIsUIBlocked', false)
      })
  },
  beforeRouteLeave: function (to, from, next) {
    this.$destroy()
    next()
  },
  beforeDestroy: function () {
    this.localInitiative = new Initiative()
    this.selectedType = new InitiativeType()
    this.currentSelectInitiativeId = new ID()
    this.nonReactive = {}
  },
  methods: {
    disableSaveButton() {
      // A new nested variant can always be saved as-is, with no field changes
      this.saveButtonDisabled = this.isCreateInitiativeCustomization ? false : true
    },
    enableSaveButton() {
      this.saveButtonDisabled = false
    },
    getTitleForTactic(tactic: Tactic) {
      let returnString = tactic.title
      if (this.isLeadPlan && tactic.abbreviatedPlanName !== '') {
        if (returnString.length > 0) {
          returnString = tactic.abbreviatedPlanName + '  |  ' + returnString
        } else {
          returnString = tactic.abbreviatedPlanName
        }
      }
      return returnString
    },
    getPlatformNamesForTactic(tactic: Tactic) {
      const tacticPlatformIntIds = tactic.tacticPlatformIds.map(
        (id: ID) => id.intID
      )
      return this.$store.getters.currentTacticPlatforms
        .filter((platform: TacticPlatform) =>
          tacticPlatformIntIds.includes(platform.id.intID)
        )
        .map((platform: TacticPlatform) => platform.name)
        .toString()
    },
    setInitialValues() {
      this.initialStartDate = JSON.stringify(this.localInitiativeCustomization.startDate)
      this.initialEndDate = JSON.stringify(this.localInitiativeCustomization.endDate)
      this.initialSummary = JSON.stringify(this.localInitiativeCustomization.summary)
    },
    setInitialConditionalValues() {
      if(this.localInitiativeCustomization?.tags){
        this.initialTags = [...this.localInitiativeCustomization.tags]
      }
    },
    compareObjects(object1, object2) {
      const keys1 = Object.keys(object1)
      const keys2 = Object.keys(object2)

      if (
        keys1.length !== keys2.length ||
        (keys1.length === 0 && keys2.length === 0)
      ) {
        this.enableSaveButton()
        return false
      }

      for (const key of keys1) {
        if (object1[key] !== object2[key]) {
          this.enableSaveButton()
          return false
        }
      }

      this.disableSaveButton()
      return true
    },
    nonConditionalChange() {
      if (
        this.initialStartDate === JSON.stringify(this.localInitiativeCustomization.startDate) &&
        this.initialEndDate === JSON.stringify(this.localInitiativeCustomization.endDate) &&
        this.initialSummary === JSON.stringify(this.localInitiativeCustomization.summary)
      ) {
        this.disableSaveButton()
      } else {
        this.enableSaveButton()
      }
    },
    showCancelConfirmation(event) {
      if (this.saveButtonDisabled) {
        this.routeToMostRecentOtherView()
      } else {
        this.$confirm.require({
          acceptLabel: 'YES',
          rejectLabel: 'NO',
          target: event.currentTarget,
          message:
            'Are you sure you want to cancel? Your changes will be lost.',
          accept: () => {
            if (this.isCreateInitiative) {
              this.currentInitiative.tags = []
            }
            this.routeToMostRecentOtherView()
          },
          reject: () => {
            // Cancel rejected
          },
        })
      }
    },
    updateEndDateInputVisibility() {
      this.shouldShowEndDateInput = this.localInitiativeCustomization.endDate.toString() !== this.localInitiativeCustomization.startDate.toString()
      this.shouldShowTimeInput = !this.localInitiativeCustomization.isAllDay
    },
    async handleSaveInitiativeCustomization(shouldPreventRouting = false) {
      // const summaryChanged = this.initialSummary !== JSON.stringify(this.localInitiativeCustomization.summary)

      return new Promise<InitiativeCustomization>((resolve) => {
        if (this.isCreateInitiativeCustomization) {
          this.$store.getters.services.initiatives
            .createCustomization(this.localInitiativeCustomization)
            .then((returnedInitiativeCustomization) => {
              Vue.prototype.$toast.add({
                severity: AlertMessageSeverity.success,
                summary: 'Nested variant created.',
                life: 3000,
              })

              // Attach associated documents
              this.localInitiativeCustomization.documents.forEach((document) => {
                returnedInitiativeCustomization.documentIds.push(document.id)
                document.initiativeCustomizations.push(returnedInitiativeCustomization.id)
                this.$store.getters.services.documents.update(document)
              })

              // Add new customization to current plan in global store
              this.$store.dispatch('addInitiativeCustomizationToCurrentPlan', returnedInitiativeCustomization)

              // if (summaryChanged && this.localInitiativeCustomization.summary) {
              //   this.$store.getters.services.initiatives
              //     .createCustomizationSummary(returnedInitiativeCustomization.id.apiID, this.localInitiativeCustomization.summary)
              //     .then((summaryData: InitiativeSummaryData) => {
              //       returnedInitiativeCustomization.initiativeSummary = summaryData
              //     })
              // }

              if (shouldPreventRouting !== true) {
                this.routeToMostRecentOtherView()
              } else {
                // Stay on this view, but adopt the new record so that subsequent
                // saves update it instead of creating another variant
                this.localInitiativeCustomization.id = returnedInitiativeCustomization.id
                this.$router.replace({
                  name: 'InitiativeCustomizationDetail',
                  params: {
                    ...this.$route.params,
                    customizationId: String(returnedInitiativeCustomization.id.intID),
                  },
                })
              }

              this.setInitialConditionalValues()
              this.setInitialValues()
              this.disableSaveButton()
              resolve(returnedInitiativeCustomization)
            })
        } else {
          this.$store.getters.services.initiatives
            .updateCustomization(this.localInitiativeCustomization)
            .then((returnedInitiativeCustomization) => {
              Vue.prototype.$toast.add({
                severity: AlertMessageSeverity.success,
                summary: 'Nested variant updated.',
                life: 3000,
              })

              // if (summaryChanged) {
              //   const existingSummary = this.localInitiativeCustomization.initiativeSummary
              //   const summaryPromise = existingSummary
              //     ? this.$store.getters.services.initiatives.updateCustomizationSummary(
              //         existingSummary.id.intID,
              //         this.localInitiativeCustomization.summary
              //       )
              //     : this.$store.getters.services.initiatives.createCustomizationSummary(
              //         returnedInitiativeCustomization.id.apiID,
              //         this.localInitiativeCustomization.summary
              //       )

              //   summaryPromise.then((summaryData: InitiativeSummaryData) => {
              //     const currentSummaryContent = this.localInitiativeCustomization.summary
              //     this.localInitiativeCustomization.initiativeSummary = summaryData
              //     returnedInitiativeCustomization.initiativeSummary = summaryData
              //     this.$store.dispatch('updateInitiativeCustomizationInCurrentPlan', returnedInitiativeCustomization).then(() => {
              //       this.localInitiativeCustomization.summary = currentSummaryContent
              //       this.setInitialConditionalValues()
              //       this.setInitialValues()
              //       this.disableSaveButton()
              //       resolve(returnedInitiativeCustomization)
              //     })
              //   })
              // } else {
                // const currentSummaryContent = this.localInitiativeCustomization.summary
                this.$store.dispatch('updateInitiativeCustomizationInCurrentPlan', returnedInitiativeCustomization).then(() => {
                  // this.localInitiativeCustomization.summary = currentSummaryContent
                  this.setInitialConditionalValues()
                  this.setInitialValues()
                  this.disableSaveButton()
                  resolve(returnedInitiativeCustomization)
                })
              // }
            })
        }
      })
    },
    handleDeleteInitiative(event) {
      this.$confirm.require({
        acceptLabel: 'YES',
        rejectLabel: 'NO',
        target: event.currentTarget,
        message: 'Are you sure you want to delete this nested variant?',
        acceptClass: 'btn-delete-accept',
        accept: () => {
          this.$store.getters.services.initiatives
            .deleteCustomization([this.localInitiativeCustomization.id.intID])
            .then(
              () => {
                this.$store.commit('updateIsUIBlocked', true)
                Vue.prototype.$toast.add({
                  severity: AlertMessageSeverity.success,
                  summary: 'Nested variant deleted.',
                  life: 3000,
                })

                // Remove customization from Initiaitve
                this.localInitiative.initiativeCustomizations = this.localInitiative.initiativeCustomizations.filter((customization) => customization.id.intID !== this.localInitiativeCustomization.id.intID)
              },
              (error) => {
                Vue.prototype.$toast.add({
                  severity: AlertMessageSeverity.error,
                  summary:
                    'There was an error deleting this initiative. Please try again.',
                })
              }
            ).finally(() => {
              this.$store.commit('updateIsUIBlocked', false)
              this.routeToMostRecentOtherView()
            })
        },
        reject: () => {
          //Delete rejected
        },
      })
    },
    routeToMostRecentOtherView() {
      this.$router.back()
    },
    onTagListUpdate(updatedList: Tag[]) {
      this.localInitiativeCustomization.tags = updatedList
      this.tagUpdateItterator += 1
      this.compareObjects(this.localInitiativeCustomization.tags, this.initialTags)
    },
    handleRemoveTag(id: ID) {
      this.localInitiativeCustomization.tags = this.localInitiativeCustomization.tags.filter(
        (tag) => tag.id.intID != id.intID
      )
      this.compareObjects(this.localInitiativeCustomization.tags, this.initialTags)
    },
    onAllDayChange() {
      // Reset hours/minutes/seconds if changing tactic to/from all-day
      this.localInitiativeCustomization.startDate.setHours(8, 0, 0, 0)
      this.localInitiativeCustomization.endDate.setHours(8, 0, 0, 0)

      // Manage time input visibility
      this.shouldShowTimeInput = !this.localInitiativeCustomization.isAllDay
    },
    onInputChange() {
      if (!this.isMonthlyOnly) {
        if (this.shouldShowEndDateInput == false) {
          this.localInitiativeCustomization.endDate = new Date(
            this.localInitiativeCustomization.startDate.toString()
          )
        }
        if (this.localInitiativeCustomization.endDate < this.localInitiativeCustomization.startDate) {
          this.localInitiativeCustomization.endDate = this.localInitiativeCustomization.startDate
        }
      }

      //TODO: currently using forceUpdate because some input components were not truggering DOM render, this is likely because their models are properties of currentTactic and not reactive, resolve this issue by ensuring that these models are reactive
      this.$forceUpdate()
    },
    onMonthlyFocusDateSelect() {
      // Ensure start/end dates for tactic span first and last day of the selected month
      this.localInitiativeCustomization.startDate.setDate(1)
      this.localInitiativeCustomization.endDate = new Date(this.localInitiativeCustomization.startDate)
      this.localInitiativeCustomization.endDate.setMonth(
        this.localInitiativeCustomization.startDate.getMonth() + 1
      )
      this.localInitiativeCustomization.endDate.setDate(0)
    },
    updateCurrentInitiativeStartDate(newStartDate) {
      this.localInitiativeCustomization.startDate = newStartDate
      this.nonConditionalChange()
      this.$forceUpdate()
    },
    updateCurrentInitiativeEndDate(newEndDate) {
      this.localInitiativeCustomization.endDate = newEndDate
      this.nonConditionalChange()
    },
    onEditorTextChange(event, refString) {
      if (
        event.htmlValue.length > 999900 &&
        event.htmlValue.length <= 1000000
      ) {
        // @ts-expect-error
        document.getElementById(refString).innerHTML =
          '<p class="warning-message">Content in this field is approaching the 1MB limit.</p>'
      } else if (event.htmlValue.length > 1000000) {
        // @ts-expect-error
        document.getElementById(refString).innerHTML =
          '<p class="error-message">Content in this field has exceeded the 1MB limit, some content will be lost.</p>'
      } else {
        // @ts-expect-error
        document.getElementById(refString).innerHTML = ''
      }
    },
    deleteDocument(event, documentIntId, customization: InitiativeCustomization) {
      this.$confirm.require({
        acceptLabel: 'Delete',
        rejectLabel: 'Cancel',
        message: `Are you sure you want to delete this document?`,
        target: event.currentTarget,
        acceptClass: 'btn-delete-accept',
        accept: () => {
          this.$store.getters.services.documents.delete(documentIntId).then(() => {
            customization.documents = customization.documents.filter(
              (doc) => doc.id.intID !== documentIntId
            )
          })
          // TODO: also delete MediaAsset?
        },
        reject: () => {
          //callback to execute when user rejects the action
        },
      })
    },
    onFileUpload(e, initiativeCustomization: InitiativeCustomization) {
      initiativeCustomization.newFile = e.target.files[0]
      initiativeCustomization.newFileName = e.target.files[0].name
    },
    uploadFile(customization) {
      const newFile = new FormData()

      if (!customization.newFile) {
        Vue.prototype.$toast.add({
          severity: AlertMessageSeverity.warn,
          summary: 'You must upload file first',
        })
        return
      }

      const file = new File([customization.newFile], customization.newFileName, {
        type: customization.newFile.type,
        lastModified: customization.newFile.lastModified,
      })
      newFile.append('file', file)

      this.$store.getters.services.users
        .mediaAsset(newFile)
        .then((newAsset) => {
          const newPlanDocument = PlanDocument.fromResponseObject({
            asset: newAsset,
            plan: this.currentPlan.id.intID,
          })
          if(customization.id.intID !== 0){
            newPlanDocument.initiativeCustomizations = [customization.id]
          }
          this.$store.getters.services.documents
            .create(newPlanDocument)
            .then((newDBPlanDocument) => {
              newPlanDocument.id = ID.fromResponseObject(
                newDBPlanDocument.id,
                'plan_documents'
              )
              // Add document to local tactic and to plan
              customization.documents.push(newPlanDocument)
              customization.documentIds.push(newPlanDocument.id)
              this.currentPlan.documents.push(newPlanDocument)

              this.localInitiativeCustomization.newFile = customization.newFile = ''
              this.localInitiativeCustomization.newFileName = customization.newFileName = ''

              if (customization.id.intID !== 0) {
                this.$store.getters.services.initiatives
                  .updateCustomization(customization)
                  .then(() => {
                    // TODO: Consider revising: This ensures that new documents are displayed when this tactic is reloaded during the same session, but that should be possible by simply updating or replacing the tactic in AppStore
                    this.$store.dispatch('refreshCurrentPlan')
                  })
              }
            })
        })
        .catch(() => {
          Vue.prototype.$toast.add({
            severity: AlertMessageSeverity.warn,
            summary:
              'There was an error uploading the file, try with a smaller file',
          })
        })
    },
  },
})
</script>

<style lang="scss">
@import '@/styles/_imports.scss';

.summary-container {
  .p-editor-container:has(.ql-disabled) {
    .ql-toolbar {
      height: 0;
      padding: 0;
      border-top: 0;
    }
  }
}
</style>
